// src/screens/MatchCalendar.tsx
import { useEffect, useMemo, useState } from "react";
import { LogoLoader } from "../components/LogoLoader";
import { Calendar, momentLocalizer, View, SlotInfo } from "react-big-calendar";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import "react-big-calendar/lib/css/react-big-calendar.css";
import moment from "moment";
import {
  CalendarDays,
  CalendarPlus,
  NotebookPen,
  CalendarRange,
  Plus,
  X,
  StickyNote,
  Trash2,
  Pencil,
} from "lucide-react";
import "moment/locale/fr";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

moment.locale("fr");
const localizer = momentLocalizer(moment);

/* ================= Types ================ */
type Match = {
  id: string;
  team_id: string;
  opponent: string;
  date: string;
  score?: string;
  victory?: boolean | null;
  location?: "domicile" | "extérieur";
};

type Training = {
  id: string;
  team_id: string;
  date: string;
  notes: string;
};

type BasicEventRow = {
  id: string;
  team_id?: string | null;
  user_id?: string | null;
  title: string;
  start: string;
  end: string;
  all_day: boolean;
  notes?: string | null;
};

type CalendarEvent = {
  id?: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: Match | Training | BasicEventRow;
  type: "match" | "training" | "basic";
  team_id?: string;
};

type TeamOpt = { id: string; name: string | null; role: string; status: string };

/* =============== Const/Utils ============== */
const DEFAULT_MATCH_MIN = 120;
const DEFAULT_TRAINING_MIN = 90;

const hasTime = (iso: string) => /T\d{2}:\d{2}/.test(iso);
const addMinutes = (d: Date, minutes: number) => new Date(d.getTime() + minutes * 60_000);
const addDays = (d: Date, days: number) => new Date(d.getTime() + days * 86_400_000);
const parseLocalDateOnly = (yyyyMmDd: string) => {
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
const toICSDateUTC = (d: Date) =>
  `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(
    d.getUTCHours()
  )}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
const escapeICS = (s: string) =>
  (s || "")
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");
const download = async (filename: string, mime: string, data: string) => {
  // ✅ Mobile Capacitor : écrire le fichier + share
  if (Capacitor.isNativePlatform()) {
    try {
      // iOS aime bien Documents ; Android OK aussi
      await Filesystem.writeFile({
        path: filename,
        data,
        directory: Directory.Documents,
        encoding: Encoding.UTF8,
        recursive: true,
      });

      const uriRes = await Filesystem.getUri({
        directory: Directory.Documents,
        path: filename,
      });

      await Share.share({
        title: "Exporter calendrier",
        text: "Voici le fichier calendrier (.ics)",
        url: uriRes.uri,
        dialogTitle: "Exporter .ics",
      });

      return;
    } catch (e) {
      console.error("ICS native export error:", e);
      alert("Export .ics impossible sur mobile (voir console).");
      return;
    }
  }

  // ✅ Web : comportement classique
  const blob = new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};


/* =============== Component =============== */
export const MatchCalendar = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>("month");
  const [date, setDate] = useState<Date>(new Date());
  const [userId, setUserId] = useState<string | null>(null);

  const [teams, setTeams] = useState<TeamOpt[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | "ALL">("ALL");
  const [isStaff, setIsStaff] = useState(false);

  const [events, setEvents] = useState<CalendarEvent[]>([]);

  // Modal création/édition événement basique
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalNotes, setModalNotes] = useState("");
  const [modalAllDay, setModalAllDay] = useState(false);
  const [modalStart, setModalStart] = useState<Date>(new Date());
  const [modalEnd, setModalEnd] = useState<Date>(addMinutes(new Date(), 60));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [modalTeamId, setModalTeamId] = useState<string | null>(null);

  // Détail événement basique
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Vue par défaut plus lisible sur mobile
  useEffect(() => {
    if (isMobile) setView("agenda");
  }, [isMobile]);


  /* ------------ Fetch: teams + events par team membership ------------- */
  useEffect(() => {
    const run = async () => {
      setLoading(true);

      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) {
        navigate("/login");
        return;
      }
      setUserId(user.id);

      // 1) Récupérer les teams où l’utilisateur est membre actif (hors équipes archivées)
      const { data: memberships, error: tmErr } = await supabase
        .from("team_members")
        .select("team_id, role, status, teams!inner(id, team_name, archived)")
        .eq("user_id", user.id)
        .eq("status", "active");

      if (tmErr) {
        console.error("team_members error:", tmErr);
      }

      const teamOpts: TeamOpt[] =
        (memberships || [])
          .filter((m: any) => !m.teams?.archived)
          .map((m: any) => ({
            id: m.team_id,
            name: m.teams?.team_name ?? null,
            role: m.role,
            status: m.status,
          })) ?? [];

      setTeams(teamOpts);

      const hasTeams = teamOpts.length > 0;
      const staffByRole = teamOpts.some((t) => ["owner", "coach", "assistant"].includes(t.role));

      // ✅ Fallback : si l'utilisateur n'a aucune équipe, on le considère en "mode solo"
      // => il est staff de ses propres données (matches/trainings avec user_id)
      setIsStaff(hasTeams ? staffByRole : true);

      const teamIds = hasTeams ? teamOpts.map((t) => t.id) : [];

      /* 2) Matches */
      let matchEvents: CalendarEvent[] = [];
      if (hasTeams) {
        const { data: matches, error: matchErr } = await supabase
          .from("matches")
          .select("id, team_id, opponent, date, score, victory, location")
          .in("team_id", teamIds);

        if (matchErr) {
          console.error("matches by team_id error:", matchErr);
        }

        matchEvents = (matches || []).map((match: Match) => {
          const emoji = match.location === "domicile" ? "🏠" : "🚌";
          const title = `${emoji} ${match.opponent}${match.score ? ` (${match.score})` : ""}`;

          if (!match.date) {
            const now = new Date();
            return {
              title,
              start: now,
              end: addMinutes(now, DEFAULT_MATCH_MIN),
              allDay: false,
              resource: match,
              type: "match",
              team_id: match.team_id,
            };
          }
          if (hasTime(match.date)) {
            const start = new Date(match.date);
            const end = addMinutes(start, DEFAULT_MATCH_MIN);
            return { title, start, end, allDay: false, resource: match, type: "match", team_id: match.team_id };
          } else {
            const start = parseLocalDateOnly(match.date.slice(0, 10));
            const end = addDays(start, 1);
            return { title, start, end, allDay: true, resource: match, type: "match", team_id: match.team_id };
          }
        });
      } else {
        // 🔙 Ancien comportement: matches par user_id
        const { data: matches, error: matchErr } = await supabase
          .from("matches")
          .select("id, team_id, opponent, date, score, victory, location")
          .eq("user_id", user.id);

        if (matchErr) {
          console.error("matches by user_id error:", matchErr);
        }

        matchEvents = (matches || []).map((match: Match) => {
          const emoji = match.location === "domicile" ? "🏠" : "🚌";
          const title = `${emoji} ${match.opponent}${match.score ? ` (${match.score})` : ""}`;

          if (!match.date) {
            const now = new Date();
            return {
              title,
              start: now,
              end: addMinutes(now, DEFAULT_MATCH_MIN),
              allDay: false,
              resource: match,
              type: "match",
              team_id: match.team_id,
            };
          }
          if (hasTime(match.date)) {
            const start = new Date(match.date);
            const end = addMinutes(start, DEFAULT_MATCH_MIN);
            return { title, start, end, allDay: false, resource: match, type: "match", team_id: match.team_id };
          } else {
            const start = parseLocalDateOnly(match.date.slice(0, 10));
            const end = addDays(start, 1);
            return { title, start, end, allDay: true, resource: match, type: "match", team_id: match.team_id };
          }
        });
      }

      /* 3) Trainings */
      let trainingEvents: CalendarEvent[] = [];
      if (hasTeams) {
        const { data: trainings, error: trErr } = await supabase
          .from("trainings")
          .select("id, team_id, date, notes")
          .in("team_id", teamIds);

        if (trErr) {
          console.error("trainings by team_id error:", trErr);
        }

        trainingEvents = (trainings || []).map((training: Training) => {
          const title = "🏋️ Entraînement";
          if (!training.date) {
            const now = new Date();
            return {
              title,
              start: now,
              end: addMinutes(now, DEFAULT_TRAINING_MIN),
              allDay: false,
              resource: training,
              type: "training",
              team_id: training.team_id,
            };
          }
          if (hasTime(training.date)) {
            const start = new Date(training.date);
            const end = addMinutes(start, DEFAULT_TRAINING_MIN);
            return {
              title,
              start,
              end,
              allDay: false,
              resource: training,
              type: "training",
              team_id: training.team_id,
            };
          } else {
            const start = parseLocalDateOnly(training.date.slice(0, 10));
            const end = addDays(start, 1);
            return { title, start, end, allDay: true, resource: training, type: "training", team_id: training.team_id };
          }
        });
      } else {
        // 🔙 Ancien comportement: trainings par user_id
        const { data: trainings, error: trErr } = await supabase
          .from("trainings")
          .select("id, team_id, date, notes")
          .eq("user_id", user.id);

        if (trErr) {
          console.error("trainings by user_id error:", trErr);
        }

        trainingEvents = (trainings || []).map((training: Training) => {
          const title = "🏋️ Entraînement";
          if (!training.date) {
            const now = new Date();
            return {
              title,
              start: now,
              end: addMinutes(now, DEFAULT_TRAINING_MIN),
              allDay: false,
              resource: training,
              type: "training",
              team_id: training.team_id,
            };
          }
          if (hasTime(training.date)) {
            const start = new Date(training.date);
            const end = addMinutes(start, DEFAULT_TRAINING_MIN);
            return {
              title,
              start,
              end,
              allDay: false,
              resource: training,
              type: "training",
              team_id: training.team_id,
            };
          } else {
            const start = parseLocalDateOnly(training.date.slice(0, 10));
            const end = addDays(start, 1);
            return { title, start, end, allDay: true, resource: training, type: "training", team_id: training.team_id };
          }
        });
      }

      /* 4) Basic events */
      let basicEvents: CalendarEvent[] = [];

      if (hasTeams) {
        const { data: basicsTry, error: basicsErr } = await supabase
          .from("basic_events")
          .select("id, team_id, user_id, title, start, end, all_day, notes")
          .or(`team_id.in.(${teamIds.join(",")}),user_id.eq.${user.id}`);

        if (!basicsErr) {
          basicEvents = (basicsTry || []).map((b: BasicEventRow) => ({
            id: b.id,
            title: `🗒️ ${b.title}`,
            start: new Date(b.start),
            end: new Date(b.end),
            allDay: !!b.all_day,
            resource: b,
            type: "basic",
            team_id: b.team_id || undefined,
          }));
        } else {
          console.error("basic_events by team_id error:", basicsErr);
          // fallback user-scoped
          const { data: basicsUser } = await supabase
            .from("basic_events")
            .select("id, user_id, title, start, end, all_day, notes")
            .eq("user_id", user.id);

          basicEvents = (basicsUser || []).map((b: BasicEventRow) => ({
            id: b.id,
            title: `🗒️ ${b.title}`,
            start: new Date(b.start),
            end: new Date(b.end),
            allDay: !!b.all_day,
            resource: b,
            type: "basic",
          }));
        }
      } else {
        // 🔙 Ancien comportement: basic_events par user_id
        const { data: basicsUser, error: basicsErr } = await supabase
          .from("basic_events")
          .select("id, user_id, title, start, end, all_day, notes")
          .eq("user_id", user.id);

        if (basicsErr) {
          console.error("basic_events by user_id error:", basicsErr);
        }

        basicEvents = (basicsUser || []).map((b: BasicEventRow) => ({
          id: b.id,
          title: `🗒️ ${b.title}`,
          start: new Date(b.start),
          end: new Date(b.end),
          allDay: !!b.all_day,
          resource: b,
          type: "basic",
        }));
      }

      setEvents([...matchEvents, ...trainingEvents, ...basicEvents]);
      setLoading(false);
    };

    run();
  }, [navigate]);

  /* ------------ Équipes où l'utilisateur est staff (peut créer/affecter) ---- */
  const staffTeams = useMemo(
    () => teams.filter((t) => ["owner", "coach", "assistant"].includes(t.role)),
    [teams]
  );

  // Équipe proposée par défaut pour un nouvel événement :
  // l'équipe filtrée si l'on en est staff, sinon la première équipe staff.
  const defaultTeamForNew = () =>
    (selectedTeamId !== "ALL" && staffTeams.some((t) => t.id === selectedTeamId)
      ? selectedTeamId
      : staffTeams[0]?.id) || null;

  /* ------------ Filtrage client par équipe (si sélectionnée) ----------- */
  const filteredEvents = useMemo(() => {
    if (selectedTeamId === "ALL") return events;
    return events.filter((ev) => !ev.team_id || ev.team_id === selectedTeamId);
  }, [events, selectedTeamId]);

  /* ---------------- Handlers UI / CRUD basic events ------------------- */
  const handleSelectEvent = (event: CalendarEvent) => {
    if (event.type === "match") navigate(`/matchdetails/${(event.resource as Match).id}`);
    else if (event.type === "training") navigate(`/trainings`);
    else if (event.type === "basic") setSelectedEvent(event);
  };

  const onSelectSlot = (slot: SlotInfo) => {
    // création d’un basic event: réservé au staff
    if (!isStaff) return;
    setEditingId(null);
    setModalTitle("");
    setModalNotes("");
    setModalTeamId(defaultTeamForNew());
    const monthAllDay = view === "month";
    setModalAllDay(monthAllDay);
    setModalStart(new Date(slot.start));
    setModalEnd(new Date(slot.end));
    setIsModalOpen(true);
  };

  const eventPropGetter = (event: CalendarEvent) => {
    if (event.type === "training") {
      return { style: { backgroundColor: "#f59e0b", color: "white", borderRadius: "6px", border: "none" } };
    }
    if (event.type === "basic") {
      return { style: { backgroundColor: "#7c3aed", color: "white", borderRadius: "6px", border: "none" } };
    }
    const match = event.resource as Match;
    let backgroundColor = "#3b82f6";
    if (match.victory === true) backgroundColor = "#22c55e";
    else if (match.victory === false) backgroundColor = "#ef4444";
    return { style: { backgroundColor, color: "white", borderRadius: "6px", border: "none" } };
  };

  const exportICS = async () => {
    const now = new Date();
    const dtstamp = toICSDateUTC(now);
    const lines: string[] = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//SwishData//MatchCalendar//FR",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
    ];

    filteredEvents.forEach((ev, idx) => {
      const uid = `${idx}-${now.getTime()}@swishdata`;
      const summary = escapeICS(ev.title);
      const description =
        ev.type === "match" ? "Match de basket" : ev.type === "training" ? "Entraînement" : "Événement";

      if (ev.allDay) {
        const start = new Date(ev.start.getFullYear(), ev.start.getMonth(), ev.start.getDate());
        const end = new Date(ev.end.getFullYear(), ev.end.getMonth(), ev.end.getDate());
        const yyyyMmDd = (d: Date) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
        lines.push(
          "BEGIN:VEVENT",
          `UID:${uid}`,
          `DTSTAMP:${dtstamp}`,
          `DTSTART;VALUE=DATE:${yyyyMmDd(start)}`,
          `DTEND;VALUE=DATE:${yyyyMmDd(end)}`,
          `SUMMARY:${summary}`,
          `DESCRIPTION:${escapeICS(description)}`,
          "END:VEVENT"
        );
      } else {
        lines.push(
          "BEGIN:VEVENT",
          `UID:${uid}`,
          `DTSTAMP:${dtstamp}`,
          `DTSTART:${toICSDateUTC(ev.start)}`,
          `DTEND:${toICSDateUTC(ev.end)}`,
          `SUMMARY:${summary}`,
          `DESCRIPTION:${escapeICS(description)}`,
          "END:VEVENT"
        );
      }
    });

    lines.push("END:VCALENDAR");

    await download(
      "swishdata_calendar.ics",
      "text/calendar;charset=utf-8",
      lines.join("\r\n")
    );
  };

  async function handleSaveBasic() {
    if (!userId) return;
    if (!modalTitle.trim()) {
      alert("Le titre est requis.");
      return;
    }
    if (!isStaff) {
      alert("Seul le staff peut créer des événements.");
      return;
    }

    // Équipe choisie dans la modale (obligatoire dès qu'on est staff d'au moins une équipe)
    const teamForEvent = modalTeamId;

    if (!teamForEvent && staffTeams.length > 0) {
      alert("Choisis une équipe pour cet événement.");
      return;
    }

    if (editingId) {
      const { data, error } = await supabase
        .from("basic_events")
        .update({
          title: modalTitle.trim(),
          start: modalStart.toISOString(),
          end: modalEnd.toISOString(),
          all_day: modalAllDay,
          notes: modalNotes.trim() || null,
          team_id: teamForEvent || null,
        })
        .eq("id", editingId)
        .select("id, team_id, title, start, end, all_day, notes")
        .maybeSingle();

      if (error || !data) {
        console.error(error);
        alert("Impossible de modifier l'événement.");
        return;
      }

      setEvents((prev) =>
        prev.map((ev) =>
          ev.id === editingId
            ? {
              ...ev,
              title: `🗒️ ${data.title}`,
              start: new Date(data.start),
              end: new Date(data.end),
              allDay: !!data.all_day,
              resource: data,
              team_id: data.team_id || undefined,
            }
            : ev
        )
      );
    } else {
      const { data, error } = await supabase
        .from("basic_events")
        .insert({
          team_id: teamForEvent || null,
          user_id: userId,
          title: modalTitle.trim(),
          start: modalStart.toISOString(),
          end: modalEnd.toISOString(),
          all_day: modalAllDay,
          notes: modalNotes.trim() || null,
        })
        .select("id, team_id, title, start, end, all_day, notes")
        .maybeSingle();

      if (error || !data) {
        console.error(error);
        alert("Impossible d'ajouter l'événement.");
        return;
      }

      const newEv: CalendarEvent = {
        id: data.id,
        title: `🗒️ ${data.title}`,
        start: new Date(data.start),
        end: new Date(data.end),
        allDay: !!data.all_day,
        resource: data,
        type: "basic",
        team_id: data.team_id || undefined,
      };
      setEvents((prev) => [...prev, newEv]);
    }

    setIsModalOpen(false);
    setEditingId(null);
  }

  async function handleDeleteBasic(id?: string) {
    if (!id) return;
    if (!isStaff) {
      alert("Seul le staff peut supprimer des événements.");
      return;
    }
    const ok = window.confirm("Supprimer cet événement ?");
    if (!ok) return;
    const { error } = await supabase.from("basic_events").delete().eq("id", id);
    if (error) {
      console.error(error);
      alert("Suppression impossible.");
      return;
    }
    setEvents((prev) => prev.filter((ev) => ev.id !== id));
    setSelectedEvent(null);
  }

  if (loading) {
    return <LogoLoader />;
  }

  /* ================= Render ================= */
  return (
    <section className="relative z-0 p-6 max-w-6xl mx-auto min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      {/* --- THEME DARK pour react-big-calendar --- */}
      <style>{`
        .dark .rbc-calendar { background-color: #0b1220; color: #e5e7eb; }
        .dark .rbc-off-range-bg { background: #0a0f1a; }
        .dark .rbc-off-range { color: #64748b; }
        .dark .rbc-today { background-color: rgba(37, 99, 235, 0.12); }
        .dark .rbc-toolbar { color: #e5e7eb; }
        .dark .rbc-toolbar button {
          color: #e5e7eb;
          background: #111827;
          border: 1px solid #374151;
          border-radius: 0.5rem;
          padding: 0.25rem 0.5rem;
        }
        .dark .rbc-toolbar .rbc-active { background: #2563eb; border-color: #1d4ed8; color: white; }
        .dark .rbc-header { background: #0f172a; color: #e5e7eb; border-bottom: 1px solid #1f2937; }
        .dark .rbc-month-row, .dark .rbc-row-bg, .dark .rbc-day-bg, .dark .rbc-agenda-view table, .dark .rbc-agenda-view thead > tr > th {
          border-color: #1f2937 !important;
        }
                  /* ===== Mobile polish ===== */
        @media (max-width: 639px) {
          .rbc-toolbar {
            display: flex;
            flex-direction: column;
            gap: 8px;
            align-items: stretch;
          }
          .rbc-toolbar .rbc-toolbar-label {
            text-align: center;
            font-weight: 700;
            padding: 6px 0;
          }
          .rbc-btn-group {
            display: flex;
            width: 100%;
          }
          .rbc-btn-group button {
            flex: 1;
            padding: 10px 8px;
            font-size: 14px;
          }
          .rbc-event {
            padding: 2px 6px;
            font-size: 12px;
            border-radius: 10px;
          }
          .rbc-month-view, .rbc-time-view, .rbc-agenda-view {
            border-radius: 16px;
            overflow: hidden;
          }
        }
      `}</style>

      {/* Header + filtres */}
      <div className="relative z-10 flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
        <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2 text-orange-600 dark:text-orange-400">
          <CalendarDays className="w-6 h-6" />
          Agenda
        </h1>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 items-stretch sm:items-center">
          {/* Sélecteur d’équipe si >1 */}
          {teams.length > 1 && (
            <select
              className="w-full sm:w-auto border rounded-xl px-3 py-2 dark:bg-gray-900 dark:border-gray-700"
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value as any)}
            >
              <option value="ALL">Toutes les équipes</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name || "Équipe"} {t.role !== "player" ? `• ${t.role}` : ""}
                </option>
              ))}
            </select>
          )}

          {/* Boutons staff */}
          {isStaff && (
            <div className="grid grid-cols-1 sm:flex sm:flex-row gap-2">
              <button
                onClick={() => navigate("/creatematch")}
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-xl font-semibold flex items-center justify-center gap-2"
              >
                <CalendarPlus className="w-5 h-5" />
                Match
              </button>
              <button
                onClick={() => navigate("/createtraining")}
                className="w-full sm:w-auto bg-yellow-600 hover:bg-yellow-700 text-white py-2 px-4 rounded-xl font-semibold flex items-center justify-center gap-2"
              >
                <NotebookPen className="w-5 h-5" />
                Entraînement
              </button>
              <button
                onClick={() => {
                  const now = new Date();
                  setEditingId(null);
                  setModalTitle("");
                  setModalNotes("");
                  setModalTeamId(defaultTeamForNew());
                  setModalAllDay(false);
                  setModalStart(now);
                  setModalEnd(addMinutes(now, 60));
                  setIsModalOpen(true);
                }}
                className="w-full sm:w-auto bg-violet-600 hover:bg-violet-700 text-white py-2 px-4 rounded-xl font-semibold flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Événement
              </button>
            </div>
          )}

          {/* Export */}
          <button
            onClick={() => exportICS()}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-xl font-semibold flex items-center justify-center gap-2"
          >
            <CalendarRange className="w-5 h-5" />
            Export .ics
          </button>
        </div>
      </div>

      {/* Mobile: switch de vue (super utile) */}
      <div className="sm:hidden mb-3">
        <div className="grid grid-cols-4 gap-2">
          {(["agenda", "month", "week", "day"] as View[]).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`py-2 rounded-xl border text-sm font-semibold ${view === v
                ? "bg-orange-600 text-white border-orange-600"
                : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800"
                }`}
            >
              {v === "agenda" ? "Agenda" : v === "month" ? "Mois" : v === "week" ? "Semaine" : "Jour"}
            </button>
          ))}
        </div>
      </div>


      <div className="relative z-0 rounded-2xl border bg-white border-gray-200 shadow-sm p-3 dark:bg-gray-900 dark:border-gray-800">
        <Calendar
          localizer={localizer}
          events={filteredEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: isMobile ? "calc(100vh - 320px)" : 650 }}
          onSelectEvent={handleSelectEvent}
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          eventPropGetter={eventPropGetter}
          culture="fr"
          showMultiDayTimes
          selectable={isStaff}
          onSelectSlot={onSelectSlot}
          popup={isMobile} // sur mobile, ouvre un popup propre pour "+X de plus"
          step={30}
          timeslots={2}
          messages={{
            today: "Aujourd'hui",
            next: "Suivant",
            previous: "Précédent",
            month: "Mois",
            week: "Semaine",
            day: "Jour",
            agenda: "Agenda",
            date: "Date",
            time: "Heure",
            event: "Événement",
            noEventsInRange: "Aucun événement prévu",
            showMore: (total) => `+ ${total}`,
          }}
        />
      </div>


      {/* Modale création/édition (basic events) */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-6">
          <div className="w-full sm:max-w-md bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-lg border border-gray-200 dark:border-gray-800">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2 font-semibold">
                <StickyNote className="w-5 h-5 text-violet-600" />
                {editingId ? "Modifier l’événement" : "Nouvel événement"}
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-4 py-4 space-y-4">
              <div className="space-y-1">
                <label className="text-sm text-gray-600 dark:text-gray-300">Titre</label>
                <input
                  className="w-full rounded-lg border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2"
                  value={modalTitle}
                  onChange={(e) => setModalTitle(e.target.value)}
                  placeholder="Ex: Réunion staff"
                />
              </div>

              {staffTeams.length > 0 && (
                <div className="space-y-1">
                  <label className="text-sm text-gray-600 dark:text-gray-300">Équipe</label>
                  <select
                    className="w-full rounded-lg border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2"
                    value={modalTeamId ?? ""}
                    onChange={(e) => setModalTeamId(e.target.value || null)}
                  >
                    {staffTeams.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name || "Équipe sans nom"}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex items-center gap-3">
                <input
                  id="allday"
                  type="checkbox"
                  checked={modalAllDay}
                  onChange={(e) => setModalAllDay(e.target.checked)}
                  className="h-4 w-4"
                />
                <label htmlFor="allday" className="text-sm">
                  Journée entière
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-sm text-gray-600 dark:text-gray-300">Début</label>
                  <input
                    type={modalAllDay ? "date" : "datetime-local"}
                    className="w-full rounded-lg border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2"
                    value={
                      modalAllDay
                        ? moment(modalStart).format("YYYY-MM-DD")
                        : moment(modalStart).format("YYYY-MM-DDTHH:mm")
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      setModalStart(
                        modalAllDay
                          ? moment(v, "YYYY-MM-DD").toDate()
                          : moment(v, "YYYY-MM-DDTHH:mm").toDate()
                      );
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm text-gray-600 dark:text-gray-300">Fin</label>
                  <input
                    type={modalAllDay ? "date" : "datetime-local"}
                    className="w-full rounded-lg border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2"
                    value={
                      modalAllDay
                        ? moment(modalEnd).format("YYYY-MM-DD")
                        : moment(modalEnd).format("YYYY-MM-DDTHH:mm")
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      setModalEnd(
                        modalAllDay
                          ? moment(v, "YYYY-MM-DD").toDate()
                          : moment(v, "YYYY-MM-DDTHH:mm").toDate()
                      );
                    }}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-sm text-gray-600 dark:text-gray-300">Notes (optionnel)</label>
                <textarea
                  className="w-full rounded-lg border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 min-h-[84px]"
                  value={modalNotes}
                  onChange={(e) => setModalNotes(e.target.value)}
                  placeholder="Détails, lieu, consignes…"
                />
              </div>
            </div>

            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveBasic}
                className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-semibold"
              >
                {editingId ? "Enregistrer" : "Ajouter"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale détail basic event */}
      {selectedEvent && selectedEvent.type === "basic" && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-6">
          <div className="w-full sm:max-w-md bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-lg border border-gray-200 dark:border-gray-800">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2 font-semibold">
                <StickyNote className="w-5 h-5 text-violet-600" />
                {selectedEvent.title.replace(/^🗒️\s*/, "")}
              </div>
              <button
                onClick={() => setSelectedEvent(null)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-4 py-4 space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {selectedEvent.allDay
                  ? `${moment(selectedEvent.start).format("DD/MM/YYYY")} → ${moment(
                    selectedEvent.end
                  ).format("DD/MM/YYYY")}`
                  : `${moment(selectedEvent.start).format("DD/MM/YYYY HH:mm")} → ${moment(
                    selectedEvent.end
                  ).format("DD/MM/YYYY HH:mm")}`}
              </p>
              {(() => {
                const tId = (selectedEvent.resource as BasicEventRow).team_id;
                const tName = teams.find((t) => t.id === tId)?.name;
                return tId ? (
                  <p className="text-xs font-medium text-violet-700 dark:text-violet-300">
                    👥 {tName || "Équipe"}
                  </p>
                ) : null;
              })()}
              {(selectedEvent.resource as BasicEventRow).notes && (
                <p className="text-gray-700 dark:text-gray-300 text-sm whitespace-pre-line">
                  {(selectedEvent.resource as BasicEventRow).notes}
                </p>
              )}
            </div>

            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
              {isStaff && (
                <>
                  <button
                    onClick={() => handleDeleteBasic(selectedEvent.id)}
                    className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold inline-flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Supprimer
                  </button>
                  <button
                    onClick={() => {
                      const b = selectedEvent.resource as BasicEventRow;
                      setEditingId(selectedEvent.id || null);
                      setModalTitle(b.title);
                      setModalNotes(b.notes || "");
                      setModalTeamId(
                        staffTeams.some((t) => t.id === b.team_id)
                          ? b.team_id!
                          : defaultTeamForNew()
                      );
                      setModalAllDay(!!b.all_day);
                      setModalStart(new Date(b.start));
                      setModalEnd(new Date(b.end));
                      setIsModalOpen(true);
                      setSelectedEvent(null);
                    }}
                    className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-semibold inline-flex items-center gap-2"
                  >
                    <Pencil className="w-4 h-4" />
                    Modifier
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
